// api.js
//
// Pen-test remediation — the JWT no longer lives in localStorage. It is
// issued by the server as an HTTP-only cookie (`noc_token`) that JavaScript
// on the page cannot read. axios sends it automatically because we set
// `withCredentials: true`.
//
// For CSRF protection, the server also issues a JS-readable cookie
// (`noc_csrf`). We echo its value in an `X-CSRF-Token` header on every
// state-changing request. The server rejects mismatches.
import axios from 'axios';
import { API_BASE_URL } from './api/config';

const CSRF_COOKIE = 'noc_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,   // send noc_token cookie on every request
});

axiosInstance.interceptors.request.use(
  (config) => {
    // Attach CSRF token on unsafe methods.
    const method = String(config.method || 'get').toUpperCase();
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      const csrf = readCookie(CSRF_COOKIE);
      if (csrf) config.headers[CSRF_HEADER] = csrf;
    }

    // FormData: let axios set the correct multipart boundary itself.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    } else if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auto-logout on 401 (token expired, session invalidated, deactivated etc).
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const msg = error?.response?.data?.message || '';
    const shouldSignOut = status === 401 || /invalid or expired token/i.test(msg);
    // Public pages must never be kicked to login on a 401 — the anonymous
    // auth/me probe returns 401 by design, which was flickering these
    // pages back to /login.
    const PUBLIC_PATHS = ['/login', '/register', '/', '/home', '/about', '/contact', '/faq', '/guidelines', '/documents', '/gallery', '/stakeholders'];
    const onPublic = PUBLIC_PATHS.includes(window.location.pathname);
    if (shouldSignOut && !onPublic) {
      try {
        // Only clear non-sensitive UI hints. Token is in an HTTP-only
        // cookie and only the server can clear it — /auth/logout does that.
        localStorage.removeItem('userRole');
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userInfo');
      } catch {}
      try {
        if (/another device|newer login|deactivated/i.test(msg)) {
          sessionStorage.setItem('logout_reason', msg);
        }
      } catch {}
      window.location.replace('/login');
    }
    return Promise.reject(error);
  }
);

export const API = {
  get: (url, config) => axiosInstance.get(url, config),
  post: (url, data, config) => axiosInstance.post(url, data, config),
  put: (url, data, config) => axiosInstance.put(url, data, config),
  delete: (url, config) => axiosInstance.delete(url, config),
};

export default API;
